import { useEffect, useState, useRef } from "react";
import { TriangleAlert, Zap, TrendingUp, Activity } from "lucide-react";

interface AnimatedStatProps {
  end: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  delay?: number;
}

const AnimatedNumber = ({ end, suffix = "", prefix = "", decimals = 0, delay = 0 }: AnimatedStatProps) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const timer = setTimeout(() => {
      setHasStarted(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [isVisible, delay]);

  useEffect(() => {
    if (!hasStarted) return;

    const duration = 3500;
    let startTime: number | null = null;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(easeOut * end);
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [hasStarted, end]);

  const displayValue = decimals > 0 ? count.toFixed(decimals) : Math.floor(count).toLocaleString();

  return (
    <span ref={ref}>
      {prefix}{displayValue}{suffix}
    </span>
  );
};

const PrecisionStatsAnimation = () => {
  const stats = [
    {
      icon: Zap,
      iconColor: "text-sky-500",
      bgColor: "bg-[#f9fafb]",
      borderColor: "border-sky-100",
      label: "Total Assets",
      value: 220,
      suffix: "",
      subtitle: "73.33 per building"
    },
    {
      icon: TriangleAlert,
      iconColor: "text-amber-500",
      bgColor: "bg-[#f9fafb]",
      borderColor: "border-amber-100",
      label: "Average Efficiency",
      value: 54,
      suffix: "%",
      badge: "3 Medium",
      badgeColor: "bg-amber-100 text-amber-700"
    },
    {
      icon: TrendingUp,
      iconColor: "text-emerald-500",
      bgColor: "bg-[#f9fafb]",
      borderColor: "border-emerald-100",
      label: "Total Annual Savings",
      value: 35200,
      prefix: "$",
      suffix: "",
      subtitle: "Combined annual OPEX savings from all projects",
      showTrend: true
    },
    {
      icon: Activity,
      iconColor: "text-rose-500",
      bgColor: "bg-[#f9fafb]",
      borderColor: "border-rose-100",
      label: "Electric EUI",
      value: 342141.5,
      suffix: "",
      decimals: 1,
      unit: "kWh/m²"
    }
  ];

  return (
    <div className="grid grid-cols-2 gap-4 h-[300px] lg:h-[400px] content-center">
      {stats.map((stat, index) => (
        <div
          key={stat.label}
          className={`${stat.bgColor} ${stat.borderColor} border rounded-xl p-4 lg:p-5 shadow-sm transition-all duration-300`}
          style={{ animationDelay: `${index * 250}ms` }}
        >
          <div className="flex items-center gap-2 mb-2">
            <stat.icon className={`w-4 h-4 ${stat.iconColor}`} />
            <span className="text-xs lg:text-sm text-muted-foreground font-medium">{stat.label}</span>
            {stat.showTrend && (
              <TrendingUp className="w-3 h-3 text-emerald-500 ml-auto" />
            )}
          </div>
          
          <div className="text-xl lg:text-2xl xl:text-3xl font-bold text-foreground">
            <AnimatedNumber 
              end={stat.value} 
              suffix={stat.suffix} 
              prefix={stat.prefix} 
              decimals={stat.decimals}
              delay={index * 350}
            />
          </div>
          
          {stat.unit && (
            <span className="text-xs text-muted-foreground">{stat.unit}</span>
          )}
          
          {stat.badge && (
            <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full ${stat.badgeColor}`}>
              {stat.badge}
            </span>
          )}
          
          {stat.subtitle && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{stat.subtitle}</p>
          )}
        </div>
      ))}
    </div>
  );
};

export default PrecisionStatsAnimation;
